/**
 * The deck's material palette.
 *
 * One shared set per renderer: white moulded shell, brushed and dark
 * aluminium, chrome, rubber and felt — plus the accent material every LED is
 * cloned from.
 */

import * as THREE from 'three';
import { brushedMetalMaps, feltNormalMap, strobeBandMap } from '../textures/surfaces.ts';

export const ACCENT = 0x2fbf71;
export const ACCENT_OFF = 0xbfc9c4;

export interface DeckMaterials {
  shellWhite: THREE.MeshPhysicalMaterial;
  shellWhiteSoft: THREE.MeshPhysicalMaterial;
  aluminium: THREE.MeshStandardMaterial;
  aluminiumDark: THREE.MeshStandardMaterial;
  chrome: THREE.MeshStandardMaterial;
  rubber: THREE.MeshStandardMaterial;
  darkPlastic: THREE.MeshPhysicalMaterial;
  felt: THREE.MeshStandardMaterial;
  strobe: THREE.MeshStandardMaterial;
  accent: THREE.MeshStandardMaterial;
  decal: (map: THREE.Texture) => THREE.MeshStandardMaterial;
}

export function buildMaterials(renderer: THREE.WebGLRenderer): DeckMaterials {
  const brushed = brushedMetalMaps(512, renderer);

  return {
    shellWhite: new THREE.MeshPhysicalMaterial({
      color: 0xf6f8f7,
      roughness: 0.42,
      metalness: 0,
      clearcoat: 0.55,
      clearcoatRoughness: 0.28,
      reflectivity: 0.4,
      sheen: 0.25,
      sheenColor: new THREE.Color(0xe9f4ee),
      sheenRoughness: 0.7,
    }),
    shellWhiteSoft: new THREE.MeshPhysicalMaterial({
      color: 0xeef2f0,
      roughness: 0.62,
      metalness: 0,
      clearcoat: 0.2,
    }),
    aluminium: new THREE.MeshStandardMaterial({
      color: 0xd9dedc,
      metalness: 1,
      roughness: 0.3,
      roughnessMap: brushed.roughnessMap,
      envMapIntensity: 1.15,
    }),
    aluminiumDark: new THREE.MeshStandardMaterial({
      color: 0xa9b0ae,
      metalness: 1,
      roughness: 0.36,
      envMapIntensity: 1,
    }),
    chrome: new THREE.MeshStandardMaterial({
      color: 0xf2f5f4,
      metalness: 1,
      roughness: 0.07,
      envMapIntensity: 1.4,
    }),
    rubber: new THREE.MeshStandardMaterial({ color: 0x2b3230, roughness: 0.88, metalness: 0 }),
    darkPlastic: new THREE.MeshPhysicalMaterial({
      color: 0x22282a,
      roughness: 0.35,
      metalness: 0,
      clearcoat: 0.8,
      clearcoatRoughness: 0.2,
    }),
    felt: new THREE.MeshStandardMaterial({
      color: 0xf3f6f4,
      roughness: 0.96,
      metalness: 0,
      normalMap: feltNormalMap(512, renderer),
      normalScale: new THREE.Vector2(0.5, 0.5),
    }),
    strobe: new THREE.MeshStandardMaterial({
      map: strobeBandMap(renderer),
      metalness: 0.85,
      roughness: 0.34,
    }),
    accent: new THREE.MeshStandardMaterial({
      color: ACCENT,
      roughness: 0.3,
      metalness: 0,
      emissive: ACCENT,
      emissiveIntensity: 0,
    }),
    /* Decals sit a hair above the plinth; the polygon offset stops them z-fighting. */
    decal: (map) =>
      new THREE.MeshStandardMaterial({
        map,
        transparent: true,
        roughness: 0.62,
        metalness: 0,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
  };
}
